import axios, { AxiosResponse, AxiosError } from 'axios';
import {
  ApiResult,
  LoginRequest,
  LoginResponse,
  ImageRecord,
  CreateImageRequest,
  UpdateImageRequest,
  ImageListQuery,
  ImageListResponse,
  WorkflowListResponse,
  CompressImageResponse,
  RestoreImageResponse,
} from '@/types';

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('upload-manager-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一错误处理
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token 过期或无效，清除本地存储并跳转到登录页
      localStorage.removeItem('upload-manager-token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API 错误处理助手
function handleApiError(error: any): ApiResult {
  if (error.response?.data) {
    return error.response.data;
  }
  
  return {
    success: false,
    error: error.message || 'Network error occurred',
    code: 'NETWORK_ERROR'
  };
}

// 认证 API
export const authApi = {
  async login(data: LoginRequest): Promise<ApiResult<LoginResponse>> {
    try {
      const response = await apiClient.post('/auth/login', data);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  async verify(): Promise<ApiResult<{ valid: boolean; decoded: any }>> {
    try {
      const response = await apiClient.post('/auth/verify');
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// 图片管理 API
export const imageApi = {
  async getImages(params: ImageListQuery): Promise<ApiResult<ImageListResponse>> {
    try {
      const response = await apiClient.get('/images', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  async getImageById(id: string): Promise<ApiResult<ImageRecord>> {
    try {
      const response = await apiClient.get(`/images/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  async createImage(data: CreateImageRequest): Promise<ApiResult<ImageRecord>> {
    try {
      const formData = new FormData();
      formData.append('workflowName', data.workflowName);
      formData.append('image', data.image);
      
      // 如果提供了accessKey，则添加到表单数据中
      if (data.accessKey) {
        formData.append('accessKey', data.accessKey);
      }

      const response = await apiClient.post('/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  async updateImage(id: string, data: UpdateImageRequest): Promise<ApiResult<ImageRecord>> {
    try {
      const formData = new FormData();
      
      if (data.workflowName !== undefined) {
        formData.append('workflowName', data.workflowName);
      }
      
      if (data.image) {
        formData.append('image', data.image);
      }

      const response = await apiClient.put(`/images/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // 一键压缩：同格式同尺寸服务端重编码，链接不变、图片不出站。
  // 不可逆——调用前必须在 UI 层做过二次确认。
  async compressImage(id: string): Promise<ApiResult<CompressImageResponse>> {
    try {
      const response = await apiClient.post(`/images/${id}/compress`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // 还原原图：把服务端备份的原始字节覆盖回同一个链接（地址不变）。
  // 同样不可逆地改变外链内容，调用前必须二次确认。
  async restoreOriginal(id: string): Promise<ApiResult<RestoreImageResponse>> {
    try {
      const response = await apiClient.post(`/images/${id}/restore`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  async deleteImage(id: string): Promise<ApiResult<null>> {
    try {
      const response = await apiClient.delete(`/images/${id}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// 工作流 API
export const workflowApi = {
  async getWorkflows(): Promise<ApiResult<WorkflowListResponse>> {
    try {
      const response = await apiClient.get('/workflows');
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// 健康检查 API
export const healthApi = {
  async check(): Promise<ApiResult<any>> {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
};

// 图片上传支持
export interface ImageUploadResult {
  url: string;
  filename: string;
  size: number;
}

// 获取图片URL（统一处理）
export function getImageUrl(imagePath: string): string {
  if (!imagePath) return '';
  
  // 如果是完整URL，直接返回
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // 处理相对路径
  const baseURL = import.meta.env.VITE_API_URL || '';
  return `${baseURL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
}

// 导出默认 API 客户端
export default apiClient;